import { useState, useCallback, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { MapPreview } from "./MapPreview";
import { AddressConfirmation } from "./AddressConfirmation";
import { useMapUrl } from "@/hooks/useMapUrl";
import { MapPin, ExternalLink, Info } from "lucide-react";

export interface AddressData {
  address: string;
  mapLink?: string;
  directions?: string;
  coordinates?: {
    lat: number;
    lng: number;
  };
}

interface AddressMapInputProps {
  value?: AddressData;
  onChange: (value: AddressData) => void;
  disabled?: boolean;
  required?: boolean;
  showDirections?: boolean;
}

export const AddressMapInput = ({
  value = { address: "", mapLink: "", directions: "" },
  onChange,
  disabled = false,
  required = false,
  showDirections = true,
}: AddressMapInputProps) => {
  const [isAddressConfirmed, setIsAddressConfirmed] = useState(false);
  const [showMapLinkInput, setShowMapLinkInput] = useState(false);
  const [addressInput, setAddressInput] = useState(value.address || "");
  const [mapLinkInput, setMapLinkInput] = useState(value.mapLink || "");
  const [directionsInput, setDirectionsInput] = useState(value.directions || "");

  const { 
    mapUrl, 
    mapEmbedUrl, 
    isLoadingKey, 
    mapsApiKey, 
    handleLocationChange 
  } = useMapUrl();

  // Update internal state when external value changes
  useEffect(() => {
    setAddressInput(value.address || "");
    setMapLinkInput(value.mapLink || "");
    setDirectionsInput(value.directions || "");
    setIsAddressConfirmed(!!value.address && !showMapLinkInput);
  }, [value, showMapLinkInput]);

  // Update map preview when address changes
  useEffect(() => {
    if (addressInput.trim()) {
      handleLocationChange(addressInput);
    }
  }, [addressInput, handleLocationChange]);

  const handleAddressChange = useCallback((newAddress: string) => {
    setAddressInput(newAddress);
    setIsAddressConfirmed(false);
    
    // Update the parent component
    const newValue: AddressData = {
      ...value,
      address: newAddress,
    };
    onChange(newValue);
  }, [value, onChange]);

  const handleMapLinkChange = useCallback((newMapLink: string) => {
    setMapLinkInput(newMapLink);
    
    // Update the parent component
    const newValue: AddressData = {
      ...value,
      mapLink: newMapLink,
    };
    onChange(newValue);
  }, [value, onChange]);

  const handleDirectionsChange = useCallback((newDirections: string) => {
    setDirectionsInput(newDirections);
    
    // Update the parent component
    const newValue: AddressData = {
      ...value,
      directions: newDirections,
    };
    onChange(newValue);
  }, [value, onChange]);

  const handleConfirmAddress = useCallback(() => {
    setIsAddressConfirmed(true);
    setShowMapLinkInput(false);
  }, []);

  const handleRejectAddress = useCallback(() => {
    setIsAddressConfirmed(false);
    setShowMapLinkInput(true);
  }, []);

  const getGoogleMapsUrl = (address: string) => {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
  };

  return (
    <div className="space-y-4">
      {/* Address Input */}
      <div className="space-y-2">
        <Label className="flex items-center gap-2">
          <MapPin className="h-4 w-4" />
          Address {required && <span className="text-destructive">*</span>}
        </Label>
        <Input
          value={addressInput}
          onChange={(e) => handleAddressChange(e.target.value)}
          placeholder="Enter your street address"
          disabled={disabled}
          className={isAddressConfirmed ? "border-green-500 bg-green-50" : ""}
        />
        <p className="text-xs text-muted-foreground">
          Enter your street address to see a map preview
        </p>
      </div>

      {/* Map Preview and Confirmation */}
      {addressInput.trim() && (
        <div className="space-y-3">
          <MapPreview
            address={addressInput}
            mapEmbedUrl={mapEmbedUrl}
            isLoadingKey={isLoadingKey}
            mapsApiKey={mapsApiKey}
          />
          
          {mapUrl && (
            <a 
              href={getGoogleMapsUrl(addressInput)}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center text-sm text-primary hover:underline"
            >
              <ExternalLink className="h-4 w-4 mr-1" />
              Open in Google Maps
            </a>
          )}

          <AddressConfirmation
            onConfirm={handleConfirmAddress}
            onReject={handleRejectAddress}
            isConfirmed={isAddressConfirmed}
            disabled={disabled}
          />
        </div>
      )}

      {/* Map Link Input (fallback) */}
      {showMapLinkInput && (
        <div className="space-y-2 p-4 border rounded-md bg-amber-50">
          <div className="flex items-center gap-2 text-amber-800">
            <Info className="h-4 w-4" />
            <p className="text-sm font-medium">Share a more accurate location</p>
          </div>
          <p className="text-xs text-amber-700">
            Open Google Maps in a separate tab.. Drop a pin on the correct location and paste the coordinates here.
          </p>
          <Label htmlFor="map-link">Google Maps Coordinates</Label>
          <Input
            id="map-link"
            value={mapLinkInput}
            onChange={(e) => handleMapLinkChange(e.target.value)}
            placeholder="Example: -1.2864, 36.8172 or https://maps.app.goo.gl/..."
            disabled={disabled}
          />
        </div>
      )}

      {/* Additional Directions */}
      {showDirections && (
        <div className="space-y-2">
          <Label htmlFor="directions">Additional Directions (optional)</Label>
          <Textarea
            id="directions"
            value={directionsInput}
            onChange={(e) => handleDirectionsChange(e.target.value)}
            placeholder="e.g., Blue gate, Next to the pharmacy, Apartment 2B, Ring the bell twice..."
            rows={3}
            disabled={disabled}
            className="resize-y"
          />
          <p className="text-xs text-muted-foreground">
            Add landmarks or specific instructions to help people find you
          </p>
        </div>
      )}
    </div>
  );
};